// @ts-check
/* ═══════════════════════ BILLETTER / WALLET-PASS (#2) ═══════════════════════
   Hurtigtilgang til fly- og cruise-referansene du faktisk åpner i køen —
   stylet som wallet-kort og tilgjengelig OFFLINE (alt er statiske data).

   Ærlig avgrensning: ekte Apple Wallet (.pkpass) og Google Wallet krever
   signering med et leverandørsertifikat på server, som vi ikke kan
   provisjonere herfra. Et signert .pkpass ville uansett trenge flyselskapets
   BCBP-strekkode, som vi ikke har — kun bookingreferansene. Derfor viser vi
   referansen i stor monospace (alltid lesbar offline) + en QR av referansen
   (caches ved første online-visning). Selve innsjekkingen gjøres fortsatt i
   flyselskapets/Celebrity-appen.
   ═══════════════════════════════════════════════════════════════════════════ */

import { esc } from './utils.js';
import { makeQR } from './qr.js';

// Flysegmenter — kilde: FLY_INFO/DAYS i data.js, samlet her som pass.
const FLIGHTS = [
  { date:'17. jul', dep:'13:50', flight:'FI319', from:'OSL', to:'KEF', airline:'Icelandair', dur:'2t 55m', ref:'Z2IIVW' },
  { date:'17. jul', dep:'17:10', flight:'FI697', from:'KEF', to:'YVR', airline:'Icelandair', dur:'7t 45m', ref:'Z2IIVW', note:'Terminal M · ankomst 17:55 lokal' },
  { date:'02. aug', dep:'12:40', flight:'WS3756', from:'YYC', to:'PDX', airline:'WestJet', dur:'2t 10m', ref:'KMXQYA' },
  { date:'02. aug', dep:'19:05', flight:'FI662', from:'PDX', to:'KEF', airline:'Icelandair', dur:'7t 35m', ref:'Z2IIVW' },
  { date:'03. aug', dep:'10:40', flight:'FI322', from:'KEF', to:'OSL', airline:'Icelandair', dur:'2t 45m', ref:'Z2IIVW', note:'Hjemme 15:25' },
];

const CRUISE = {
  ship:'Celebrity Solstice', line:'Celebrity Cruises',
  cabin:'AquaClass 1563 · Dekk 11', booking:'6158158',
  embark:'19. jul · Vancouver', disembark:'26. jul · Vancouver',
};

// Nicholas reiser på samme familietur, men med EGEN bestilling (USA Tours
// NO13827): egne referanser, egen lugar og en annen retur (via Toronto, ikke
// Portland). Holdes adskilt så referansene ikke blandes i køen.
const NICHOLAS_FLIGHTS = [
  { date:'17. jul', dep:'13:50', flight:'FI319', from:'OSL', to:'KEF', airline:'Icelandair', dur:'2t 55m', ref:'YUDTUF' },
  { date:'17. jul', dep:'17:10', flight:'FI697', from:'KEF', to:'YVR', airline:'Icelandair', dur:'7t 45m', ref:'YUDTUF', note:'Terminal M · ankomst 17:55 lokal' },
  { date:'02. aug', dep:'14:10', flight:'WS640',  from:'YYC', to:'YYZ', airline:'WestJet',    dur:'3t 55m', ref:'CBNLIG', note:'Terminal 3 · ankomst 20:05' },
  { date:'02. aug', dep:'23:10', flight:'FI604',  from:'YYZ', to:'KEF', airline:'Icelandair', dur:'5t 35m', ref:'YUDTUF', note:'Terminal 3 · ankomst 03. aug 08:45' },
  { date:'03. aug', dep:'10:40', flight:'FI322',  from:'KEF', to:'OSL', airline:'Icelandair', dur:'2t 45m', ref:'YUDTUF', note:'Hjemme 15:25' },
];

const NICHOLAS_CRUISE = {
  ship:'Celebrity Solstice', line:'Celebrity Cruises',
  cabin:'Veranda 6108 · Dekk 6', booking:'7202457',
  embark:'19. jul · Vancouver', disembark:'26. jul · Vancouver',
};

function passCard(inner, accent){
  return `<div class="pass-card" style="--pass-accent:${accent}">${inner}</div>`;
}

// QR settes inn etter render (DOM-element), så vi gir hvert pass en mount-id.
let _qrTargets = [];

function flightPass(f, i, prefix=''){
  const id = `pass-qr-${prefix}f${i}`;
  _qrTargets.push({ id, text: f.ref });
  return passCard(`
    <div class="pass-top">
      <div class="pass-air">${esc(f.airline)}</div>
      <div class="pass-date">${esc(f.date)}</div>
    </div>
    <div class="pass-route">
      <div class="pass-port"><span class="pass-code">${esc(f.from)}</span></div>
      <div class="pass-arrow">✈</div>
      <div class="pass-port"><span class="pass-code">${esc(f.to)}</span></div>
    </div>
    <div class="pass-meta">
      <div><span class="pass-lbl">Avgang</span><span class="pass-val">${esc(f.dep)}</span></div>
      <div><span class="pass-lbl">Flight</span><span class="pass-val">${esc(f.flight)}</span></div>
      <div><span class="pass-lbl">Varighet</span><span class="pass-val">${esc(f.dur)}</span></div>
    </div>
    ${f.note ? `<div class="pass-note">${esc(f.note)}</div>` : ''}
    <div class="pass-foot">
      <div class="pass-ref"><span class="pass-lbl">Referanse</span><span class="pass-refcode">${esc(f.ref)}</span></div>
      <div class="pass-qr" id="${id}"></div>
    </div>`, '#0E2238');
}

function cruisePass(c, prefix=''){
  const id = `pass-qr-${prefix}cruise`;
  _qrTargets.push({ id, text: c.booking });
  return passCard(`
    <div class="pass-top">
      <div class="pass-air">⚓ ${esc(c.line)}</div>
      <div class="pass-date">${esc(c.embark.split(' · ')[0])}</div>
    </div>
    <div class="pass-ship">${esc(c.ship)}</div>
    <div class="pass-meta">
      <div><span class="pass-lbl">Lugar</span><span class="pass-val">${esc(c.cabin)}</span></div>
    </div>
    <div class="pass-meta">
      <div><span class="pass-lbl">Embarkering</span><span class="pass-val">${esc(c.embark)}</span></div>
      <div><span class="pass-lbl">I land</span><span class="pass-val">${esc(c.disembark)}</span></div>
    </div>
    <div class="pass-foot">
      <div class="pass-ref"><span class="pass-lbl">Booking</span><span class="pass-refcode">${esc(c.booking)}</span></div>
      <div class="pass-qr" id="${id}"></div>
    </div>`, '#2E6B5C');
}

// Ett sett billetter per reisende/bestilling.
function travelerPasses(b){
  const cruise = b.cruise ? cruisePass(b.cruise, b.prefix) : '';
  const flights = b.flights.map((f,i)=>flightPass(f, i, b.prefix)).join('');
  return `
  ${b.heading ? `<div class="sec-lbl" style="margin-top:26px">${esc(b.heading)}</div>` : ''}
  ${b.sub ? `<div class="page-sub" style="margin:-4px 0 10px">${esc(b.sub)}</div>` : ''}
  ${cruise ? `<div class="sec-lbl">⚓ Cruise</div>${cruise}` : ''}
  <div class="sec-lbl" style="margin-top:18px">✈️ Fly</div>
  ${flights}`;
}

export function renderPasses(){
  _qrTargets = [];
  const main = travelerPasses({ prefix:'', flights:FLIGHTS, cruise:CRUISE });
  const nicholas = travelerPasses({
    prefix:'n-', flights:NICHOLAS_FLIGHTS, cruise:NICHOLAS_CRUISE,
    heading:'👤 Nicholas – egen bestilling',
    sub:'USA Tours NO13827 · egne referanser, lugar og retur via Toronto',
  });
  return `
  <div class="page-title">🎟️ Billetter</div>
  <div class="page-sub">Referansene du trenger i køen — fungerer offline</div>
  <div class="pass-disclaimer">📲 Selve innsjekkingen gjøres i Icelandair-/WestJet-/Celebrity-appen. Her ligger referansene klare uten nett.</div>
  ${main}
  ${nicholas}`;
}

// Kalles etter at HTML er i DOM — fyller inn QR-kodene.
export function bindPasses(){
  for (const t of _qrTargets){
    const el = document.getElementById(t.id);
    if (el && !el.firstChild) {
      el.appendChild(makeQR(t.text, 64));
    }
  }
}
